import React, { useState } from "react";
import { IExpense, IInvoice } from "../../../@types";
import { CloseIcon } from "../../../assets/icons";
import { useParams } from "react-router-dom";
import { useProjectHook } from "../../../hooks/project/useProject";
import ListItem from "./ListItem";
import { useProjectStore } from "../../../state";

interface AddExistingModalProps {
  isOpen: boolean;
  type: string;
  onClose: () => void;
  clientName: string;
  existingInvoicesData: IInvoice[];
  existingExpensesData: IExpense[];
  fetchProjectData: () => Promise<void>;
}

const AddExistingModal: React.FC<AddExistingModalProps> = ({
  isOpen,
  type,
  onClose,
  clientName,
  existingInvoicesData,
  existingExpensesData,
  fetchProjectData,
}) => {
  const { getProjectById } = useProjectStore();
  const { projectId } = useParams<{ projectId: any }>();
  const { toggleProjectInvoice, toggleProjectExpense, isRemoving } =
    useProjectHook({
      projectId: projectId,
    });
  const [selectedDataId, setSelectedDataId] = useState<string>("");

  // Filter out added invoices and invoices that already have a projectId
  const existingFilteredInvoices = existingInvoicesData.filter(
    (invoiceData) => !invoiceData.projectId,
  );
  const existingFilteredExpenses = existingExpensesData.filter(
    (expenseData) => !expenseData.projectId,
  );

  const handleInvoiceClick = (invoice: IInvoice) => {
    setSelectedDataId(invoice.id);
    // console.log("selected invoice Id:", invoice);
  };
  const handleExpenseClick = (expense: IExpense) => {
    setSelectedDataId(expense.id);
    // console.log("selected expense Id:", expense);
  };

  const handleAddExistingExpense = async (expenseId: string) => {
    const res = await toggleProjectExpense({
      expenseId: expenseId,
      value: true,
      projectId,
    });
    if (res) {
      setSelectedDataId("");
      onClose();
      getProjectById(projectId);
      fetchProjectData();
    }
  };

  const handleAddExistingInvoice = async (invoiceId: string) => {
    const res = await toggleProjectInvoice({
      invoiceId: invoiceId,
      value: true,
      projectId,
    });
    if (res) {
      setSelectedDataId("");
      onClose();
      getProjectById(projectId);
      fetchProjectData();
    }
  };

  const handleAddExistingClick = () => {
    if (type == "expense") {
      handleAddExistingExpense(selectedDataId);
    } else {
      handleAddExistingInvoice(selectedDataId);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 h-full min-h-[100svh] bg-black bg-opacity-50 md:flex items-center justify-center z-50 overflow-y-auto p-4"
      onClick={() => {
        onClose();
        setSelectedDataId("");
      }}
    >
      <div
        className="w-full max-w-[500px] mx-auto bg-white rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="lg:p-5 p-4">
          <div className="flex">
            <h2 className="flex-1 text-xl font-bold text-gray-900 mb-2">
              Add {type}
            </h2>
            <div
              onClick={onClose}
              className="flex justify-end mb-[-3em] cursor-pointer"
            >
              <CloseIcon />
            </div>
          </div>
          <p className="text-gray-700 mb-4">
            {`Select from the ${type}s generated for`}
            <span className="font-semibold text-black"> {clientName}</span>
          </p>

          <div className="h-full max-h-[300px] overflow-y-auto mb-6">
            {type === "expense" && existingFilteredExpenses.length > 0 ? (
              existingFilteredExpenses.map((expense) => (
                <ListItem
                  key={expense.id}
                  id={expense.id}
                  primaryText={expense.name || ""}
                  amount={expense.amount}
                  isSelected={selectedDataId === expense.id}
                  onClick={() => handleExpenseClick(expense)}
                />
              ))
            ) : (
              <p className="text-center text-gray-700 font-medium my-2">
                {type === "expense" && "No existing expense found"}
              </p>
            )}
            {type === "invoice" && existingFilteredInvoices.length > 0 ? (
              existingFilteredInvoices.map((invoice) => (
                <ListItem
                  key={invoice.id}
                  id={invoice.id}
                  primaryText={invoice.invoiceNumber || ""}
                  amount={invoice.total || 0}
                  isSelected={selectedDataId === invoice.id}
                  onClick={() => handleInvoiceClick(invoice)}
                />
              ))
            ) : (
              <p className={`text-center text-gray-700 font-medium my-2`}>
                {type === "invoice" && "No existing invoice found"}
              </p>
            )}
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleAddExistingClick}
              className={`bg-[#044E97] text-white font-medium md:py-3 py-2 md:px-10 px-8 rounded-md transition-colors ${isRemoving || !selectedDataId ? "bg-opacity-60" : "hover:bg-blue-700"}`}
              disabled={isRemoving || !selectedDataId}
            >
              {isRemoving ? "Adding..." : "Continue"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddExistingModal;
